/**
 * Everything the system has produced, and a way to download it again.
 *
 * The list comes from the bucket rather than from any record, because two of the three kinds
 * of document leave no record at all: an Excel export and a board paper are written to S3 and
 * hand back a link, and that link was the only copy she had. Anything generated before this
 * screen existed is therefore here too.
 *
 * Links are minted when the list loads, not stored — so the button on screen works, and a row
 * left open all afternoon is refreshed by reloading rather than failing at the download.
 *
 * Deleting is the one thing in the app that destroys something. The bucket is not versioned and
 * two of the three kinds leave no record anywhere, so a deleted board paper is gone in every
 * sense — which is why the red cross asks first, in the row itself rather than in a browser
 * dialog, and why the confirm names the file it is about to remove.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { formatDateTime } from "../config";
import type { GeneratedDocument } from "../types";
import { DocumentActions } from "./DocumentView";

/**
 * How long a generated document survives before the lifecycle rule removes it.
 *
 * Mirrors `RETENTION_DAYS` in `AssessNow.tsx` and `var.export_retention_days` in
 * `infra/variables.tf` — three places, one number, and they have to move together. Said on screen
 * because until 27 August 2026 it was seven days and nobody was told: every assessment ever
 * generated had already been deleted while the screen went on offering links to them.
 */
const RETENTION_DAYS = 365;

const KIND_LABELS: Record<GeneratedDocument["kind"], string> = {
  assessment: "Impact assessment",
  "board paper": "Board paper",
  export: "Excel export",
  document: "Document",
};

function size(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function GeneratedDocuments({
  onError,
  refresh = 0,
}: {
  onError: (error: unknown) => void;
  /** Bumped by the screen when something new may be in the bucket — a run that just finished. */
  refresh?: number;
}) {
  const [docs, setDocs] = useState<GeneratedDocument[] | null>(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  /** The document whose red cross has been clicked, waiting for her to mean it. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(() => {
    setBusy(true);
    api
      .listDocuments()
      .then((page) => {
        setDocs(page.items);
        setTotal(page.total);
      })
      .catch(onError)
      .finally(() => setBusy(false));
  }, [onError]);

  useEffect(load, [load, refresh]);

  const remove = async (doc: GeneratedDocument) => {
    setDeleting(doc.ref);
    try {
      await api.deleteDocument(doc.ref);
      // Drop the row rather than reloading: reloading re-signs every other link for no reason,
      // and the count beneath has to come down with it or it reads as a failed delete.
      setDocs((current) => (current ?? []).filter((row) => row.ref !== doc.ref));
      setTotal((current) => Math.max(0, current - 1));
      setConfirming(null);
    } catch (error) {
      onError(error);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="card">
      <div className="row documents-head">
        <h3>Documents you have generated</h3>
        <button className="link" disabled={busy} onClick={load}>
          {busy ? "Refreshing…" : "Refresh links"}
        </button>
      </div>

      {docs === null ? (
        <p className="small muted">Loading…</p>
      ) : docs.length === 0 ? (
        <p className="small muted">
          Nothing generated yet. Board papers, Excel exports and impact assessments all appear
          here once you have run one.
        </p>
      ) : (
        <>
          <table className="documents">
            <thead>
              <tr>
                <th scope="col">Document</th>
                <th scope="col">Kind</th>
                <th scope="col">Generated</th>
                <th scope="col">Size</th>
                <th scope="col">
                  <span className="visually-hidden">Delete</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => (
                <tr key={doc.ref} className={confirming === doc.ref ? "confirming" : undefined}>
                  <td>
                    <a href={doc.url} target="_blank" rel="noreferrer noopener">
                      {doc.filename}
                    </a>
                    <DocumentActions url={doc.url} filename={doc.filename} />
                  </td>
                  <td className="muted">{KIND_LABELS[doc.kind]}</td>
                  <td className="muted">{formatDateTime(doc.created_at)}</td>
                  <td className="muted numeric">{size(doc.size)}</td>
                  <td className="delete-cell">
                    {confirming === doc.ref ? (
                      <span className="confirm-delete">
                        <span className="small">Delete for good?</span>
                        <button
                          type="button"
                          className="danger"
                          disabled={deleting === doc.ref}
                          onClick={() => remove(doc)}
                        >
                          {deleting === doc.ref ? "Deleting…" : "Delete"}
                        </button>
                        <button
                          type="button"
                          className="link"
                          disabled={deleting === doc.ref}
                          onClick={() => setConfirming(null)}
                        >
                          cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="delete-cross"
                        title={`Delete ${doc.filename} from AWS. There is no undo.`}
                        aria-label={`Delete ${doc.filename}`}
                        onClick={() => setConfirming(doc.ref)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="small muted">
            {total > docs.length
              ? `Showing the ${docs.length} most recent of ${total}. `
              : ""}
            Links expire after fifteen minutes — use Refresh links if one stops working. The{" "}
            <strong>files themselves are deleted after {RETENTION_DAYS} days</strong>, so download
            anything you want to keep. Impact assessments cost model time to produce and are not
            regenerated automatically; board papers and Excel exports can be run again from above.
          </p>
        </>
      )}
    </div>
  );
}
